import {Buffer} from 'buffer'

import {Jurisdiction} from './countries/constants'

export const maxFileSize = 5 * 1024 * 1024
export const maxTimeoutMs = 2147483647

export enum Locale {
  en = 'en',
  sk = 'sk',
  cs = 'cs',
  es = 'es',
  de = 'de',
  fr = 'fr',
  it = 'it',
  pl = 'pl',
  pt = 'pt',
}

export const partiallySupportedLocales = [Locale.de, Locale.fr, Locale.it, Locale.pl, Locale.pt]

export const isPartiallySupportedLocale = (locale: string | undefined) =>
  Boolean(locale && partiallySupportedLocales.includes(locale as Locale))

export const isRestrictedLocale = (locale: string | undefined): locale is string =>
  Boolean(locale && [Locale.en, Locale.es].includes(locale as Locale))

export const isFullySupportedLocale = (locale: string | undefined) =>
  Boolean(
    locale &&
      Object.values(Locale).includes(locale as Locale) &&
      !partiallySupportedLocales.includes(locale as Locale),
  )

export const fullySupportedLocales = Object.values(Locale).filter(isFullySupportedLocale)

export const allLocales = Object.values(Locale)

export const euipoRepresentationTypes = {
  word: {value: 'WORD', label: 'word'},
  figurative: {value: 'FIGURATIVE', label: 'figurative'},
  stylized_characters: {value: 'STYLIZED_CHARACTERS', label: 'stylized characters'},
  shape: {value: 'SHAPE', label: 'shape'},
  combined_shape: {value: 'COMBINED_SHAPE', label: 'combined shape'},
  sound: {value: 'SOUND', label: 'sound'},
}

export const euipoApplicantType = {
  individual: {value: 'APPLICANT_INDIVIDUAL', label: 'Individual'},
  business: {value: 'APPLICANT_BUSINESS', label: 'Business'},
  reference: {value: 'REFERENCE', label: 'Existing EUIPO applicant'},
}

// TODO: refactor to use region instead of locale/language
export const localeToNationalCountry = {
  [Locale.sk]: 'sk',
  [Locale.cs]: 'cz',
  [Locale.en]: 'gb',
  [Locale.es]: 'es',
  [Locale.de]: 'de',
  [Locale.fr]: 'fr',
  [Locale.it]: 'it',
  [Locale.pl]: 'pl',
  [Locale.pt]: 'pt',
}

export const localeToJurisdiction = {
  [Locale.sk]: Jurisdiction.SK,
  [Locale.cs]: Jurisdiction.CZ,
  [Locale.en]: Jurisdiction.UK,
  [Locale.es]: Jurisdiction.ES,
  [Locale.de]: Jurisdiction.DE,
  [Locale.fr]: Jurisdiction.FR,
  [Locale.it]: Jurisdiction.IT,
  [Locale.pl]: Jurisdiction.PL,
  [Locale.pt]: Jurisdiction.PT,
}

export const localeToTranslationKey = {
  [Locale.en]: 'page.language-en',
  [Locale.sk]: 'page.language-sk',
  [Locale.cs]: 'page.language-cs',
  [Locale.es]: 'page.language-es',
  [Locale.de]: 'page.language-de',
  [Locale.fr]: 'page.language-fr',
  [Locale.it]: 'page.language-it',
  [Locale.pl]: 'page.language-pl',
  [Locale.pt]: 'page.language-pt',
}

export enum PaymentType {
  card = 'card',
  transfer = 'transfer',
  // bad name for gpay/apple pay
  wallet = 'wallet',
}

export const beneluxCountriesCodes = ['BE', 'NL', 'LU']
export const euCountriesCodes = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'BX',
]

export const trademarkAbandonedStatus = {
  applicationRefused: 'Refused',
  applicationAbandoned: 'Abandoned',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
  expired: 'Expired',
  rejected: 'Rejected',
  filingAbandoned: 'Filing abandoned',
} as const

export type TrademarkAbandonedStatus =
  typeof trademarkAbandonedStatus[keyof typeof trademarkAbandonedStatus]

export const trademarkStatus = {
  createdFromDivide: 'Created from a divide',
  souSubmitted: 'SOU submitted',
  souExtended: 'SOU extended',
  divided: 'Divided',
  readyForFiling: 'Ready for filing',
  submittedToPartner: 'Submitted to partner',
  applicationFiled: 'Application filed',
  officeActionReceived: 'Office action received',
  officeActionResponseSubmitted: 'Response to office action submitted',
  tmPublished: 'Trademark published',
  applicationOpposed: 'Application opposed',
  statementOfUseRequested: 'Statement of use requested',
  tmRegistered: 'Trademark registered',
  underExamination: 'Under examination',
  suspended: 'Suspended',
  declarationOfUseRequested: 'Declaration of use requested',
  douSubmitted: 'DOU submitted',
  renewalRequested: 'Renewal requested',
  renewalSubmitted: 'Renewal submitted',
  filingPostponed: 'Filing postponed',
  ...trademarkAbandonedStatus,
} as const

// why a trademark was pulled out of the 'Ready for filing' queue
export const trademarkFilingPostponedReason = {
  clientInputNeeded: 'Client input needed',
  partnerIssue: "Issue on Partner's side",
  other: 'Other',
} as const

export type TrademarkFilingPostponedReason =
  typeof trademarkFilingPostponedReason[keyof typeof trademarkFilingPostponedReason]

// trademark number is not known in these stages
export const trademarkEarlyStatuses: string[] = [
  trademarkStatus.readyForFiling,
  trademarkStatus.filingPostponed,
  trademarkStatus.submittedToPartner,
  trademarkStatus.createdFromDivide,
]

export const trademarkPreRegistrationStatuses: string[] = [
  ...trademarkEarlyStatuses,
  trademarkStatus.submittedToPartner,
  trademarkStatus.applicationFiled,
]

export const trademarkFinalStatuses: string[] = [
  ...Object.values(trademarkAbandonedStatus),
  trademarkStatus.tmRegistered,
]

// when creating trademark in admin, we only allow to pick from the following statuses:
export const trademarkCreateAllowedStatuses: string[] = [
  trademarkStatus.applicationAbandoned,
  trademarkStatus.applicationFiled,
  trademarkStatus.applicationOpposed,
  trademarkStatus.applicationRefused,
  trademarkStatus.cancelled,
  trademarkStatus.expired,
  trademarkStatus.filingAbandoned,
  trademarkStatus.officeActionReceived,
  trademarkStatus.officeActionResponseSubmitted,
  trademarkStatus.statementOfUseRequested,
  trademarkStatus.suspended,
  trademarkStatus.tmPublished,
  trademarkStatus.tmRegistered,
  trademarkStatus.underExamination,
  trademarkStatus.withdrawn,
  trademarkStatus.readyForFiling,
]

export const trademarkAction = {
  brandRegistryCodeSent: 'Brand registry code sent',
  brandRegistryConfirmed: 'Brand registry confirmed',
  certificateSent: 'Certificate sent',
  representativeChanged: 'Representative changed',
  applicationRefunded: 'Refunded',
  trademarkNumberIssued: 'Trademark number issued',
  similarTrademarksNotice: 'Similar trademarks notice',
  renewalOrdered: 'Renewal ordered',
  renewalFirstReminderSent: 'Renewal first reminder sent',
  renewalSecondReminderSent: 'Renewal second reminder sent',
  renewalThirdReminderSent: 'Renewal third reminder sent',
  renewalFourthReminderSent: 'Renewal fourth reminder sent',
  paidToPartner: 'Paid to partner',
  apiFilingStarted: 'Filing via API started',
  brandMonitoringUpsellSent: 'Brand monitoring upsell sent',
  similarTrademarksNoticeResponded: 'Similar trademark notice - responded',
} as const

export type TrademarkAction = typeof trademarkAction[keyof typeof trademarkAction]

export const trademarkStatusGroups = {
  abandoned: 'Abandoned',
  readyForFiling: 'Ready for filing',
  filing: 'Filing',
  completed: 'Completed',
  refunded: 'Refunded',
}

export const applicantPersonTypes = {
  physical: 'physical',
  physicalContractor: 'physical-contractor',
  legal: 'legal',
}

export type ApplicantType = typeof applicantPersonTypes[keyof typeof applicantPersonTypes]

// We do not use 'physical-contractor' as entity type anymore
export enum EntityPersonType {
  physical = 'physical',
  legal = 'legal',
}

export enum EntityPath {
  ownerEntity = 'owner_entity',
  billingEntity = 'billing_entity',
  representativeEntity = 'representative_entity',
}

export const ABTestNames = {
  sanityCheck: 'SanityCheck',
}

export enum TrademarkType {
  text = 'text',
  image = 'image',
}

export enum VerificationTrademarkTypeValue {
  text = 'text',
  image = 'image',
  logo = 'logo',
}

export enum TrademarkTypeValue {
  text = 'text',
  image = 'image',
  combined = 'combined',
}

export const verificationToTrademarkTypes = {
  [TrademarkTypeValue.text]: [TrademarkType.text],
  [TrademarkTypeValue.image]: [TrademarkType.image],
  [TrademarkTypeValue.combined]: [TrademarkType.text, TrademarkType.image],
}

export type QueryLink = {
  pathname: string
  query: Record<string, string | string[] | undefined>
}

export enum TrademarkRequestStatus {
  created = 'created',
  processed = 'processed',
}

export type UploadedFile = {
  name: string
  type: string
  size: number
  data: string
}

export type DeserializedFile = {
  name: string
  type: string
  size: number
  data: Buffer
}

export type RaFile = {
  rawFile: UploadedFile
  src: string
  title: string
}

export const vpnAccessDisabledHttpStatusCode = 418

export enum DiscountCouponEntity {
  registrationRequest = 'RegistrationRequest',
  brandSubscription = 'BrandSubscription',
  serviceRequest = 'ServiceRequest',
}

export enum PaymentCardBrand {
  visa = 'visa',
  mastercard = 'mastercard',
  amex = 'amex',
}

export const paymentCardBrandToTitle = {
  [PaymentCardBrand.visa]: 'Visa',
  [PaymentCardBrand.mastercard]: 'Mastercard',
  [PaymentCardBrand.amex]: 'American Express',
}

export enum EmailFeedback {
  noTime = 'no-time',
  betterDeal = 'better-deal',
  noIceCream = 'no-ice-cream',
}
